import { Button } from "@/components/shadcn/button";
import { NoxusAlert } from "@/components/UI/NoxusAlert";
import { useEditor, EditorContent, mergeAttributes } from "@tiptap/react";
import { Document } from "@tiptap/extension-document";
import { Paragraph } from "@tiptap/extension-paragraph";
import { Text as TextExtension } from "@tiptap/extension-text";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import suggestion from "./suggestion";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { RichTextValue } from "../RichTextField";
import CustomMention from "./CustomMention";
import DialogEditor from "./DialogTipTapEditor";
import useEditorEventListeners from "./useEditorEventListeners";
import { NodeSelection } from "@tiptap/pm/state";

import "./styles.css";
import ToolbarButton from "./ToolbarButton";
import { Markdown } from "tiptap-markdown";
import { debounce } from "lodash";
import History from "@tiptap/extension-history";

import { AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { Textarea } from "@/components/shadcn/textarea";
import { handleChipClickRaw, injectMentionsIntoDoc } from "./chipUtils";
import {
  parseMentionToken,
  getEditorMentionIds,
  getEditorMentionClasses,
  chipOptionsToSuggestionItems,
  allChipValues,
  hasChipValues,
  type AdvancedChipOption,
  type MentionSuggestionItem,
} from "./chipOptionsConfig";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/shadcn/dropdown-menu";
import { Sparkles } from "lucide-react";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/shadcn/tooltip";
import { ChipInsertDropdown, ChipBadgeList } from "./MentionList";
import { getModifierKey } from "@/utils/generic";
import { SpotToast } from "@/components/SpotToast";
import { sanitizeTiptapContent } from "./sanitize";
import {
  PLAIN_TEXT_BLOCK_SEPARATOR,
  fitsPlainTextSchema,
  plainTextToDoc,
} from "./plainText";
import type { Editor } from "@tiptap/core";

function transformMentions(text: string): string {
  const mentionPattern =
    /<span data-type="mention"[^>]*?data-id="([^"]+)"[^>]*?data-prefix="([^"]*)"[^>]*>[^<]+<\/span>/g;
  return text.replace(mentionPattern, (_, dataId, prefix) => {
    return prefix ? `((${prefix}${dataId}))` : `((${dataId}))`;
  });
}

// Simple-text mode has no marks, no blocks beyond paragraphs and no
// markdown input or paste rules: a backtick or `*` in a SQL query stays a
// character instead of becoming formatting.
export function buildExtensions(placeholder?: string, isSimpleText = false) {
  return [
    ...(isSimpleText
      ? [Document, Paragraph, TextExtension]
      : [StarterKit.configure({ history: false })]),
    CustomMention.configure({
      HTMLAttributes: {
        class: "mention",
        draggable: "true",
      },
      suggestion: {
        ...suggestion,
        items: ({ query, editor }): MentionSuggestionItem[] => {
          const suggestionItems: MentionSuggestionItem[] =
            // @ts-expect-error suggestionItems is a custom prop
            editor.options.editorProps.suggestionItems || [];
          return suggestionItems.filter((item) =>
            item.id.toLowerCase().startsWith(query.toLowerCase())
          );
        },
      },
      renderText({ node }) {
        const prefix = node.attrs.prefix || "";
        return `((${prefix}${node.attrs.id}))`;
      },
      renderHTML({ options, node }) {
        const color = node.attrs.color;
        const hasColor = color && color !== "default";
        const nodeImage = node.attrs.nodeImage as string | null;
        const nodeColor = node.attrs.nodeColor as string | null;
        const nodeName = node.attrs.nodeName as string | null;
        const nodeKind = node.attrs.nodeKind as "node" | "secret" | null;
        const label = node.attrs.label as string | null;
        // Prefer the friendly label (e.g. "Response") over the raw id
        // (which may be a V2 `<uuid>.output` ref) for the chip's value
        // section. Runtime resolution still uses `id`; this only affects
        // what the user sees.
        const displayValue = label || (node.attrs.id as string);
        const hasSourceMeta = !!(
          nodeImage ||
          nodeColor ||
          nodeName ||
          nodeKind === "secret"
        );

        if (hasSourceMeta) {
          const sourceChildren: any[] = [
            [
              "span",
              {
                class: "mention-source-avatar",
                style: nodeColor ? `background-color:${nodeColor};` : undefined,
                contenteditable: "false",
              },
              ...(nodeImage
                ? [
                    [
                      "img",
                      {
                        src: nodeImage,
                        alt: "",
                        class: "mention-source-avatar-image",
                        draggable: "false",
                      },
                    ],
                  ]
                : nodeKind === "secret"
                  ? [
                      [
                        "span",
                        {
                          class: "mention-source-avatar-key",
                          "aria-hidden": "true",
                        },
                        "🔑",
                      ],
                    ]
                  : []),
            ],
          ];
          if (nodeName) {
            sourceChildren.push([
              "span",
              {
                class: "mention-source-name",
                contenteditable: "false",
              },
              nodeName,
            ]);
          }
          return [
            "span",
            {
              ...mergeAttributes(options.HTMLAttributes),
              class: `mention mention--source-chip ${node.attrs.class || ""}`,
            },
            [
              "span",
              {
                class: "mention-source-section",
                contenteditable: "false",
              },
              ...sourceChildren,
            ],
            [
              "span",
              {
                class: "mention-value-section",
              },
              displayValue,
            ],
          ];
        }

        if (hasColor) {
          return [
            "span",
            {
              ...mergeAttributes(options.HTMLAttributes),
              class: getEditorMentionClasses(color, node.attrs.class),
              "data-icon": node.attrs.icon || "",
            },
            displayValue,
          ];
        }

        return [
          "span",
          {
            ...mergeAttributes(options.HTMLAttributes),
            class: `mention ${node.attrs.class || ""}`,
          },
          displayValue,
        ];
      },
    }),
    History,
  ];
}

export function getEditorText(editor: Editor, isSimpleText?: boolean) {
  return isSimpleText
    ? editor.getText({ blockSeparator: PLAIN_TEXT_BLOCK_SEPARATOR })
    : transformMentions(editor.storage.markdown.getMarkdown());
}

function valueToContent(
  value: RichTextValue | undefined,
  isSimpleText?: boolean
) {
  if (Object.keys(value?.definition || {}).length > 0) {
    const definition = sanitizeTiptapContent(value?.definition ?? {});
    // A definition saved by the markdown editor (a code mark from backticks,
    // a list, a hardBreak) cannot load into the simple-text schema, and TipTap
    // would mount an empty field. The text is what runs, so show that.
    if (!isSimpleText || fitsPlainTextSchema(definition)) return definition;
  }
  if (isSimpleText) return plainTextToDoc(value?.text || "");
  return value?.text || "";
}

export interface ITiptapEditorConfig {
  isExpandable?: boolean;
  hideToolbar?: boolean;
  numberLines?: number;
  noSpaceOnInsert?: boolean;
  hideChipOptions?: boolean;
  toolbarButtons?: (
    | "code"
    | "list"
    | "template"
    | "raw"
    | "expand"
    | "bold"
    | "italic"
  )[];
}

interface TemplateOption {
  label: string;
  value: string;
}

const NUM_LINES = [
  "one-line",
  "two-lines",
  "three-lines",
  "four-lines",
  "five-lines",
];

interface ITiptapEditor {
  onChange: (value: RichTextValue) => any;
  chipOptions?: AdvancedChipOption[];
  value?: RichTextValue;
  fullSize?: boolean;
  unselectAllNodes?: () => any;
  placeholder?: string;
  isControlled?: boolean;
  label?: string;
  config?: ITiptapEditorConfig;
  isSimpleText?: boolean;
  isDisabled?: boolean;
  isColored?: boolean;
  showMutedOverlay?: boolean;
  templateOptions?: TemplateOption[];
  onKeyDown?: (e: React.KeyboardEvent) => void;
}

const TiptapEditor = ({
  onChange,
  chipOptions,
  value,
  fullSize = false,
  unselectAllNodes,
  placeholder,
  isControlled = false,
  label,
  config,
  isSimpleText,
  isDisabled = false,
  isColored,
  showMutedOverlay,
  templateOptions,
  onKeyDown,
}: ITiptapEditor) => {
  const [isRaw, setIsRaw] = useState<boolean>(!!value?.isRaw);

  const numberLinesClass =
    config?.numberLines && NUM_LINES[config?.numberLines - 1]
      ? NUM_LINES[config?.numberLines - 1]
      : "";

  const suggestionItems = useMemo(
    () => chipOptionsToSuggestionItems(chipOptions ?? []),
    [chipOptions]
  );

  const allValues = useMemo(
    () => allChipValues(chipOptions ?? []),
    [chipOptions]
  );

  const extensions = useMemo(
    () => buildExtensions(placeholder, isSimpleText),
    [placeholder, isSimpleText]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedOnChange = useCallback(
    debounce((editor) => {
      onChange({
        text: getEditorText(editor, isSimpleText),
        definition: editor.getJSON(),
      });
    }, 500),
    [onChange]
  );

  const editor = useEditor({
    extensions: isSimpleText
      ? [...extensions, Placeholder.configure({ placeholder })]
      : [...extensions, Placeholder.configure({ placeholder }), Markdown],
    editorProps: {
      handleKeyDown: (view, event) => {
        if (onKeyDown) {
          onKeyDown(event as unknown as React.KeyboardEvent);
        }
        return false;
      },
      attributes: {
        class: `tiptap-editor-custom ${numberLinesClass} ${
          isDisabled ? "disabled" : ""
        }`,
      },
      // @ts-expect-error suggestionItems is a custom prop used by the mention extension
      suggestionItems: suggestionItems,
    },
    content: valueToContent(value, isSimpleText),
    onUpdate({ editor }) {
      if (isRawRef.current) return;
      if (isControlled) {
        onChange({
          text: getEditorText(editor, isSimpleText),
          definition: editor.getJSON(),
        });
      } else {
        debouncedOnChange(editor);
      }
    },
    onSelectionUpdate({ editor }) {
      const { from, to } = editor.state.selection;

      if (editor.state.selection instanceof NodeSelection) {
        return;
      }

      editor.state.doc.descendants((node, pos) => {
        if (node.type.name === "mention") {
          const mentionFrom = pos;
          const mentionTo = pos + node.nodeSize;
          const isFullySelected = from <= mentionFrom && to >= mentionTo;

          editor.commands.command(({ tr }) => {
            tr.setMeta("addToHistory", false);

            if (isFullySelected) {
              tr.setNodeMarkup(pos, null, { ...node.attrs, class: "selected" });
            } else {
              tr.setNodeMarkup(pos, null, { ...node.attrs, class: null });
            }
            return true;
          });
        }
      });
    },
    immediatelyRender: false,
  });

  const isRawRef = useRef<boolean>(!!value?.isRaw);
  useEffect(() => {
    isRawRef.current = !!value?.isRaw;
  }, [value?.isRaw]);

  useEffect(() => {
    setIsRaw(!!value?.isRaw);
  }, [value?.isRaw]);

  useEffect(() => {
    if (editor && editor.isEditable !== !isDisabled) {
      editor.setEditable(!isDisabled, false);
    }
  }, [editor, isDisabled]);

  useEffect(() => {
    if (isControlled && editor) {
      const tags_on_editor = getEditorMentionIds(editor.getJSON());

      const invalidTags = tags_on_editor.filter(
        (tag) => !allValues.some((chip) => chip === tag)
      );

      if (invalidTags.length > 0 || allValues.length === 0) {
        const { from, to } = editor.state.selection;

        editor
          .chain()
          .setContent(valueToContent(value, isSimpleText))
          .setTextSelection({ from, to })
          .run();
      }
    }
  }, [value, isControlled, editor, allValues, chipOptions, isSimpleText]);

  const {
    addCodeBlock,
    addListBlock,
    addMention,
    addReferenceMention,
    toggleBold,
    toggleItalic,
  } = useEditorEventListeners({
    editor,
    suggestionItems: chipOptionsToSuggestionItems(chipOptions ?? []),
    unselectAllNodes,
    fullSize,
    noSpaceOnInsert: config?.noSpaceOnInsert || false,
  });

  const [isOpen, setIsOpen] = useState(false);

  const rawTextareaRef = useRef<HTMLTextAreaElement | null>(null);

  const text = isRaw ? value?.text : editor?.getText();

  const activeTags = useMemo(() => {
    if (!isRaw && editor) {
      return getEditorMentionIds(editor.getJSON());
    }
    return Array.from(
      new Set(
        text
          ?.match(/\(\(@?[^()]*?\)\)/g)
          ?.map((tag) => parseMentionToken(tag).id) || []
      )
    );
  }, [text, isRaw, editor]);

  const invalidTags = useMemo(() => {
    return Array.from(
      new Set(activeTags.filter((tag) => !allValues.includes(tag)) || [])
    );
  }, [activeTags, allValues]);

  const handleModalSave = (newContent: RichTextValue) => {
    onChange(newContent);
    if (editor) {
      editor.commands.setContent(valueToContent(newContent, isSimpleText));
    }
  };

  const convertRawToDefinition = (plain: string) => {
    if (!plain || plain.length === 0) return {} as Record<string, any>;
    if (isSimpleText) {
      const doc = injectMentionsIntoDoc(
        plainTextToDoc(plain),
        chipOptions ?? []
      );
      editor?.commands.setContent(doc);
      return doc as Record<string, any>;
    }
    if (editor) {
      editor.commands.setContent(plain);
      const parsed = editor.getJSON();
      const withMentions = injectMentionsIntoDoc(parsed, chipOptions ?? []);
      editor.commands.setContent(withMentions);
      return withMentions;
    }
    return {
      type: "doc",
      content: [
        { type: "paragraph", content: [{ type: "text", text: plain }] },
      ],
    } as Record<string, any>;
  };

  // One-time: on editor mount, convert ((id)) text tokens to mention nodes.
  // We gate on the editor's CURRENT text (after content load) rather than
  // `value.definition` because the backend strips definition during persistence
  // — `getTemplateStringValue` then rehydrates the text into plain paragraphs,
  // so the definition exists but still contains literal `((id))` strings that
  // need conversion.
  const didInitInjectRef = useRef(false);
  useEffect(() => {
    if (!editor || didInitInjectRef.current) return;
    const currentText = editor.getText();
    if (!currentText.includes("((")) return;
    didInitInjectRef.current = true;
    const withMentions = injectMentionsIntoDoc(
      editor.getJSON(),
      chipOptions ?? []
    );
    editor.commands.setContent(withMentions, false);
  }, [editor]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTemplateSelect = (templateValue: string) => {
    if (!templateValue) return;

    const nextDefinition = convertRawToDefinition(templateValue);
    if (editor && Object.keys(nextDefinition).length > 0) {
      editor.commands.setContent(nextDefinition);
    }
    onChange({
      text: templateValue,
      definition: nextDefinition,
      isRaw: false,
    });
  };

  const toggleRawMode = () => {
    if (isRaw) {
      const nextDefinition = convertRawToDefinition(value?.text || "");
      setIsRaw(false);
      if (editor && Object.keys(nextDefinition).length > 0) {
        editor.commands.setContent(nextDefinition);
      }
      onChange({
        text: value?.text || "",
        definition: nextDefinition,
        isRaw: false,
      });
    } else {
      setIsRaw(true);
      onChange({ text: value?.text || "", definition: {}, isRaw: true });
    }
  };

  const hasChipOptions = hasChipValues(chipOptions);

  const chipDropdownButton = (
    <ChipInsertDropdown
      items={suggestionItems}
      isDisabled={isDisabled || !hasChipOptions}
      onSelect={(item) => {
        if (isRaw) {
          handleChipClickRaw(item.id, {
            textareaRef: rawTextareaRef,
            currentText: value?.text || "",
            prefix: item.prefix,
            noSpaceOnInsert: config?.noSpaceOnInsert,
            setText: (next) =>
              onChange({ text: next, definition: {}, isRaw: true }),
          });
        } else {
          addMention(item.id, item.prefix, item.color, {
            label: item.label,
            nodeImage: item.sourceImage,
            nodeColor: item.sourceColor,
            nodeName: item.sourceName,
            nodeKind: item.sourceKind,
          });
        }
      }}
    />
  );

  const extraToolbarContent =
    hasChipOptions && !isDisabled ? (
      <div className="flex items-center gap-1">
        {chipDropdownButton}
        <Separator orientation="vertical" />
      </div>
    ) : undefined;

  return (
    <div
      className="flex flex-col w-full flex-1 max-h-full"
      aria-disabled={isDisabled}
    >
      {/* {!config?.hideChipOptions && hasChipOptions && (
        <ChipBadgeList
          chipOptions={chipOptions ?? []}
          activeTags={activeTags}
          isDisabled={isDisabled}
          onSelect={(item) => {
            if (isRaw) {
              handleChipClickRaw(item.id, {
                textareaRef: rawTextareaRef,
                currentText: value?.text || "",
                prefix: item.prefix,
                noSpaceOnInsert: config?.noSpaceOnInsert,
                setText: (next) =>
                  onChange({ text: next, definition: {}, isRaw: true }),
              });
            } else {
              addMention(item.id, item.prefix, item.color, {
                label: item.label,
                nodeImage: item.sourceImage,
                nodeColor: item.sourceColor,
                nodeName: item.sourceName,
                nodeKind: item.sourceKind,
              });
            }
          }}
        />
      )} */}

      <div
        className={cn(
          "flex flex-col flex-1 border border-input rounded-md overflow-hidden relative max-h-full cursor-auto shadow-sm",
          "focus-within:ring-ring focus-within:ring-1 focus-within:outline-none",
          isDisabled ? "cursor-not-allowed" : "cursor-auto opacity-100"
        )}
      >
        {showMutedOverlay && (
          <div className="absolute top-0 left-0 right-0 bottom-0 bg-editing-overlay/50 z-1 border-2 border-editing-overlay-border rounded-md pointer-events-none flex items-end justify-end p-2 pr-2" />
        )}

        {!config?.hideToolbar && (
          <Toolbar
            addCodeBlock={addCodeBlock}
            addListBlock={addListBlock}
            toggleBold={toggleBold}
            toggleItalic={toggleItalic}
            onOpen={() => setIsOpen(true)}
            fullSize={fullSize}
            config={config}
            isDisabled={isDisabled}
            isRaw={isRaw}
            onToggleRaw={toggleRawMode}
            templateOptions={templateOptions}
            onTemplateSelect={handleTemplateSelect}
            extraToolbarContent={extraToolbarContent}
            editor={editor}
            isColored={isColored}
          />
        )}

        <div
          className={cn(
            "editor-container relative resize-none p-4 flex-1 overflow-auto dark:bg-input/30  bg-background",
            isDisabled ? "pointer-events-none" : "pointer-events-auto",
            fullSize ? "max-h-full" : "max-h-100",
            config?.hideChipOptions && hasChipOptions ? "py-2 px-3" : "p-4",
            config?.hideToolbar && hasChipOptions && !isDisabled && "pr-10"
          )}
        >
          {config?.hideToolbar && hasChipOptions && !isDisabled && (
            <div className="absolute top-2 right-2 z-10">
              {chipDropdownButton}
            </div>
          )}
          {isRaw ? (
            <Textarea
              className="w-full h-full resize-none outline-none bg-transparent p-0 border-none shadow-none focus-visible:ring-0 min-h-24"
              value={value?.text || ""}
              onKeyDown={(e) => {
                if (onKeyDown) {
                  onKeyDown(e);
                }
              }}
              onChange={(e) => {
                const next = e.target.value;
                onChange({ text: next, definition: {}, isRaw: true });
              }}
              onSelect={(e) => {
                const ta = e.target as HTMLTextAreaElement;
                void ta.selectionStart;
              }}
              disabled={isDisabled}
              ref={rawTextareaRef}
            />
          ) : (
            <EditorContent editor={editor} />
          )}
        </div>
        <AnimatePresence>
          {invalidTags.length > 0 && (
            <div className="p-2">
              <NoxusAlert
                look="banner"
                variant="warning"
                title={`The following tags are not valid: ${invalidTags.join(
                  ", "
                )}`}
              />
            </div>
          )}
        </AnimatePresence>
      </div>
      {isOpen && (
        <DialogEditor
          isOpen={isOpen}
          onOpenChange={setIsOpen}
          content={value || { text: "", definition: {} }}
          onSave={handleModalSave}
          chipOptions={chipOptions}
          unselectAllNodes={unselectAllNodes}
          label={label}
          isSimpleText={isSimpleText}
        />
      )}
    </div>
  );
};

export default TiptapEditor;

interface IToolbar {
  addCodeBlock: () => any;
  addListBlock: () => any;
  toggleBold: () => any;
  toggleItalic: () => any;
  fullSize: boolean;
  onOpen: () => any;
  config?: ITiptapEditorConfig;
  isDisabled?: boolean;
  isRaw?: boolean;
  onToggleRaw?: () => any;
  templateOptions?: TemplateOption[];
  onTemplateSelect?: (value: string) => any;
  editor: any;
  extraToolbarContent?: React.ReactNode;
  isColored?: boolean;
}

export const Toolbar = ({
  addCodeBlock,
  addListBlock,
  toggleBold,
  toggleItalic,
  fullSize,
  onOpen,
  config,
  isDisabled,
  isRaw,
  onToggleRaw,
  templateOptions,
  onTemplateSelect,
  editor,
  extraToolbarContent,
  isColored = true,
}: IToolbar) => {
  const handleTemplateSelect = (templateValue: string) => {
    onTemplateSelect?.(templateValue);
  };

  const showButton = (
    buttonType: NonNullable<ITiptapEditorConfig["toolbarButtons"]>[number]
  ) => {
    if (!config?.toolbarButtons) return true;
    return config.toolbarButtons.includes(buttonType);
  };

  return (
    <div
      aria-disabled={isDisabled}
      className={cn(
        "flex px-3 py-1.5 gap-4 border-b border-input justify-between bg-background2",
        isColored ? "bg-background2" : "bg-background"
      )}
    >
      <div className="flex gap-1">
        {showButton("bold") && (
          <ToolbarButton
            type="bold"
            label={`Bold (${getModifierKey()}B)`}
            onClick={toggleBold}
            isDisabled={isDisabled || !!isRaw}
            isActive={editor?.isActive("bold")}
          />
        )}
        {showButton("italic") && (
          <ToolbarButton
            type="italic"
            label={`Italic (${getModifierKey()}I)`}
            onClick={toggleItalic}
            isDisabled={isDisabled || !!isRaw}
            isActive={editor?.isActive("italic")}
          />
        )}
        {(showButton("bold") || showButton("italic")) && (
          <div className="w-px h-4 bg-border mx-1 self-center" />
        )}
        {showButton("code") && (
          <ToolbarButton
            type="code"
            label="Add code block"
            onClick={addCodeBlock}
            isDisabled={isDisabled || !!isRaw}
          />
        )}
        {showButton("list") && (
          <ToolbarButton
            type="list"
            label="Add list block (or press * to start a new list)"
            onClick={addListBlock}
            isDisabled={isDisabled || !!isRaw}
          />
        )}
      </div>
      <div className="flex gap-2 items-center">
        {extraToolbarContent}
        <div className="flex gap-2">
          {showButton("template") &&
            templateOptions &&
            templateOptions.length > 0 && (
              <DropdownMenu>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="iconsm"
                        isDisabled={isDisabled}
                        tabIndex={-1}
                      >
                        <Sparkles className="size-4" strokeWidth={1.25} />
                      </Button>
                    </DropdownMenuTrigger>
                  </TooltipTrigger>
                  <TooltipContent side="top">Load template</TooltipContent>
                </Tooltip>
                <DropdownMenuContent align="end">
                  {templateOptions.map((template) => (
                    <DropdownMenuItem
                      key={template.value}
                      onClick={() => handleTemplateSelect(template.value)}
                    >
                      {template.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          {showButton("raw") && (
            <ToolbarButton
              type="raw"
              label={isRaw ? "Show rich text" : "Show raw"}
              onClick={onToggleRaw || (() => {})}
              isActive={isRaw}
            />
          )}
          <ToolbarButton
            type="copy"
            label={`Copy (${getModifierKey()}C)`}
            onClick={() => {
              SpotToast.success("Copied to clipboard");
              const html = editor?.getHTML() || "";
              const text = editor?.getText() || "";
              const htmlBlob = new Blob([html], { type: "text/html" });
              const textBlob = new Blob([text], { type: "text/plain" });
              navigator.clipboard.write([
                new ClipboardItem({
                  "text/html": htmlBlob,
                  "text/plain": textBlob,
                }),
              ]);
            }}
          />
        </div>
        {showButton("expand") && !fullSize && config?.isExpandable && (
          <ToolbarButton type="expand" label="Expand" onClick={onOpen} />
        )}
      </div>
    </div>
  );
};

import { Separator } from "@/components/shadcn/separator";
